import type { PlatformAlertListResponse } from '@oip/contracts';
import type {
  LogDashboardResponse,
  LogLevelSetRequest,
  LogLevelState,
  LogDetailResponse,
  LogSearchResponse,
  LogTimelineResponse,
} from '@oip/contracts';
import type {
  PolicyTemplateListResponse,
  VehicleImportReport,
  CreatePartnerResellerRequest,
  DryRunRequest,
  DryRunResponse,
  MoveCompanyResellerRequest,
  PartnerResellerDto,
  PartnerResellerFeaturesResponse,
  PartnerResellerListResponse,
  RenamePartnerResellerRequest,
  SetPartnerResellerFeatureRequest,
  SetPartnerResellerStatusRequest,
  InvitationSubjectResponse,
} from '@oip/contracts';
import type {
  AnomalyFamilySettingsResponse,
  OperatingCalendarResponse,
  UpdateAnomalyFamilies,
  AiConsentResponse,
  UpdateAiConsent,
  UpdateOperatingCalendar,
} from '@oip/contracts';
import type {
  AnomalyOverviewResponse,
  AnomalyFindingListQuery,
  AnomalyFindingListResponse,
  CreateFleetNewsNote,
  DailyHeadlinesResponse,
  FleetNewsNoteDto,
  UpdateFleetNewsNote,
  QueryDocumentInput,
  QueryResultResponse,
  CopilotAskRequestInput,
  CopilotUsageResponse,
  CopilotFindingDto,
  CopilotInvestigationDto,
  PolicyBacktestResponse,
  ConsolidatedFleetSummaryResponse,
  SubTenantLifecycleRequest,
  TransferVehicleRequest,
  TransferVehicleResponse,
  UnresolvedVehiclesResponse,
  CreateSubTenantRequest,
  CreateSubTenantResponse,
  SubTenantDto,
  SubTenantListResponse,
  UpdateSubTenantRequest,
  InboundEventQuery,
  InboundEventListResponse,
  InboundEventDetail,
  CreateProviderConnectionRequest,
  ProviderConnectionSecretResponse,
  VehicleTachoResponse,
  TachoActivityResponse,
  TachoVehiclesResponse,
  VehicleTripsResponse,
  TelemetryStatesResponse,
  TenantThemeDto,
} from '@oip/contracts';
import type {
  MyNotificationSettings,
  UpdateMyNotifications,
  AuthoriseSendingDomain,
  BeginVerification,
  BeginVerificationResponse,
  CredentialRotationResult,
  CredentialRotationStatus,
  CreateScheduledReport,
  ReportDownload,
  ScheduledReportDto,
  ScheduledReportRunsResponse,
  ScheduledReportsResponse,
  UpdateScheduledReport,
  RevokeSendingDomain,
  SendingDomainsResponse,
  ConfirmVerification,
  DeliveryHistoryResponse,
  TenantNotificationPolicyDto,
  UpdateTenantNotificationPolicy,
  UpdateAlertCategoryPolicy,
  DiagnoseRequest,
  DiagnoseResponse,
  PlatformChannelsResponse,
  UpdatePlatformChannel,
  SuppressionsResponse,
  PreviewRequest,
  PreviewResponse,
  TestSend,
  TestSendResponse,
  BroadcastPreviewRequest,
  BroadcastPreviewResponse,
  SendBroadcast,
  SendBroadcastResponse,
  TenantBrandingDto,
  UpdateTenantBranding,
  UpdateChannelProvider,
  VerificationResult,
  TenantSmtpDto,
  UpdateTenantSmtp,
} from '@oip/contracts';
import type {
  CohortComparisonResponse,
  CreateVehicleClassRequest,
  FleetCanResponse,
  UpdateVehicleClassRequest,
  VehicleClassDto,
  CreateDriversHoursRuleset,
  DriversHoursRulesetList,
  UpdateDriversHoursRuleset,
  VehicleClassList,
  TelemetryHealthResponse,
  TelemetryTrendResponse,
  VehicleTrackResponse,
  GeofenceListResponse,
  GeofenceDto,
  VersionResponse,
  AiAllowance,
  RecommendationActionResponse,
  PolicyLearningResponse,
  BulkRecommendationActionResponse,
  TenantAiBudgetResponse,
  AnomalyPublicationResponse,
  UpdateAnomalyPublication,
  UpdateAiBudget,
  FleetPositionsResponse,
  TelemetryHistoryResponse,
  EvidenceAttachment,
  EvidenceDownloadUrlResponse,
  EvidenceListResponse,
  InitiateEvidenceRequest,
  InitiateEvidenceResponse,
  BusinessImpactSummaryResponse,
  ConfidenceStatsResponse,
  EvidenceCoverageResponse,
  OutcomeTrendBucket,
  OutcomeTrendResponse,
  OutcomeSummaryResponse,
  PerformerStatsResponse,
  RootCauseStatsResponse,
  TimingStatsResponse,
  AdminPermissionsResponse,
  AdminRolesResponse,
  AdminTenantSettingsResponse,
  AdminMemberDto,
  AdminMemberListQuery,
  CreateMembershipRequest,
  UpdateMembershipRequest,
  AdminMemberListResponse,
  AuditListQuery,
  AuditListResponse,
  CreatePolicyRequest,
  CreatePolicyVersionRequest,
  FleetEventListResponse,
  IncidentActionResponse,
  IncidentDetail,
  IncidentSummaryResponse,
  IncidentListQuery,
  IncidentListResponse,
  LlmExecutionDetail,
  LlmExecutionListQuery,
  LlmExecutionListResponse,
  LlmExplanationHistoryResponse,
  CreatePromptRequest,
  CreatePromptVersionRequest,
  LlmPromptDto,
  PromptKeyOverrideRequest,
  LlmPromptVersionDto,
  LlmPromptsResponse,
  UpdatePromptVersionRequest,
  LlmSettingsResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  CreatePlatformUserRequest,
  CreateTenantRequest,
  PlatformUserDto,
  PlatformUserCreated,
  PlatformUserListQuery,
  PlatformAuditListQuery,
  PlatformAuditListResponse,
  PipelineHealthResponse,
  FleetSummaryResponse,
  FleetTachoResponse,
  FleetSummaryQuery,
  CanOperationsResponse,
  CanOperationsQuery,
  UnitPreferencesResponse,
  ReportedSeriesFields,
  FeatureKey,
  TenantFeaturesResponse,
  PartnerUnitDefaults,
  UpdatePartnerUnitDefaults,
  UpdateUnitPreferences,
  LoginSessionsResponse,
  ActiveSessionsResponse,
  RevokeSessionsResponse,
  UpdateTenantFeature,
  PlatformUserListResponse,
  UpdatePlatformUserRequest,
  TenantDetail,
  TenantListQuery,
  TenantListResponse,
  TenantProvisioningResponse,
  UpdateTenantRequest,
  LoginResponse,
  MeResponse,
  PolicyDetail,
  PolicyLifecycleRequest,
  PolicyListItem,
  PolicyListQuery,
  PolicyListResponse,
  PolicyVersionDto,
  PolicyVersionListResponse,
  UpdatePolicyVersionRequest,
  ProviderConnectionsResponse,
  EventProcessingHealthResponse,
  UpdateProviderOutbound,
  ProviderSyncResult,
  StoreProviderCredential,
  AddExternalRecipient,
  AddExternalRecipientResponse,
  ExternalRecipientDto,
  RecommendationDetail,
  RecommendationHistoryResponse,
  RecommendationListQuery,
  RecommendationListResponse,
  RecommendationOutcome,
  RecordOutcomeRequest,
  RegenerateExplanationResponse,
  CorrelationConfigVersionDto,
  CorrelationConfigVersionList,
  CreateCorrelationConfigVersionRequest,
  CreateDriverRequest,
  DriverDetail,
  DriverListItem,
  DriverListQuery,
  DriverListResponse,
  UpdateDriverRequest,
  CreateVehicleRequest,
  UpdateVehicleRequest,
  VehicleDetail,
  VehicleEventListQuery,
  VehicleListQuery,
  VehicleListResponse,
  WhiteLabelListQuery,
  WhiteLabelListResponse,
  PlatformPartnerCompaniesResponse,
  WhiteLabelPartnerDto,
  WhiteLabelFeaturesResponse,
  CreateWhiteLabelRequestInput,
  CreateWhiteLabelResponse,
  UpdateWhiteLabelRequest,
  UpdateWhiteLabelFeature,
  PartnerContextResponse,
  PartnerCompanyListResponse,
  CreatePartnerCompanyRequest,
  CreatePartnerCompanyResponse,
  PartnerBrandingDto,
  UpdatePartnerBranding,
  PartnerMemberListResponse,
  AddPartnerMemberRequest,
  AddPartnerMemberResponse,
  UpdatePartnerMemberRequest,
} from '@oip/contracts';
import { api, type ListParams, downloadWithSession } from './client';

const V1 = '/api/v1';
type Q = Record<string, unknown>;

export const endpoints = {
  // auth
  login: (email: string, password: string) =>
    api.post<LoginResponse>(`${V1}/auth/login`, { email, password }, { noTenant: true }),
  me: () => api.get<MeResponse>(`${V1}/auth/me`),
  /**
   * `skipSessionExpiry`: a 401 here means the CURRENT password was wrong, not
   * that the session ended. Without this the global handler would sign the user
   * out for a typo.
   */
  /**
   * Ends the session on the SERVER as well as locally.
   *
   * Without this, clearing localStorage leaves a refresh token valid for thirty
   * days — signing out on a shared machine would look like it worked and would
   * not have.
   */
  logout: (refreshToken?: string) =>
    api.post<void>(`${V1}/auth/logout`, refreshToken ? { refreshToken } : {}),

  changePassword: (body: ChangePasswordRequest) =>
    api.post<ChangePasswordResponse>(`${V1}/auth/change-password`, body, {
      skipSessionExpiry: true,
    }),

  // incidents
  incidents: (q: ListParams<IncidentListQuery>) =>
    api.get<IncidentListResponse>(`${V1}/incidents`, q as Q),
  /** One branch's incidents, read from the organisation without switching in. */
  incidentsAs: (tenantId: string, q: ListParams<IncidentListQuery>) =>
    api.getAs<IncidentListResponse>(`${V1}/incidents`, tenantId, q as Q),
  incident: (id: string) => api.get<IncidentDetail>(`${V1}/incidents/${id}`),
  /**
   * Ask for an AI rewording of one incident.
   *
   * ⚠️ A POST because it COMPUTES and may spend the organisation's allowance, not because it
   * necessarily writes — an unchanged incident returns its stored wording and calls nothing.
   * It can answer `WORKING`: the work happens in the worker (the API may not import the
   * gateway), so a slow model returns before the wording lands and the page refetches.
   */
  incidentSummarise: (id: string) =>
    api.post<IncidentSummaryResponse>(`${V1}/incidents/${id}/summary`, {}),
  incidentAction: (id: string, action: 'acknowledge' | 'resolve' | 'close', note?: string) =>
    api.post<IncidentActionResponse>(`${V1}/incidents/${id}/${action}`, { note }),
  incidentAssign: (id: string, assigneeUserId: string, note?: string) =>
    api.post<IncidentActionResponse>(`${V1}/incidents/${id}/assign`, { assigneeUserId, note }),

  // recommendations
  recommendations: (q: ListParams<RecommendationListQuery>) =>
    api.get<RecommendationListResponse>(`${V1}/recommendations`, q as Q),
  /** One branch's recommendations, read from the organisation. */
  recommendationsAs: (tenantId: string, q: ListParams<RecommendationListQuery>) =>
    api.getAs<RecommendationListResponse>(`${V1}/recommendations`, tenantId, q as Q),
  recommendation: (id: string) => api.get<RecommendationDetail>(`${V1}/recommendations/${id}`),
  recommendationHistory: (id: string) =>
    api.get<RecommendationHistoryResponse>(`${V1}/recommendations/${id}/history`),
  recommendationAction: (id: string, action: string, body: { note?: string }) =>
    api.post<RecommendationActionResponse>(`${V1}/recommendations/${id}/${action}`, body),
  bulkRecommendationAction: (action: string, body: { ids: string[]; note?: string }) =>
    api.post<BulkRecommendationActionResponse>(`${V1}/recommendations/bulk/${action}`, body),
  // 404 here means "no outcome recorded yet", which is the normal state for most
  // recommendations. The hook translates it; see useRecommendationOutcome.
  recommendationOutcome: (id: string) =>
    api.get<RecommendationOutcome>(`${V1}/recommendations/${id}/outcome`),
  recordRecommendationOutcome: (id: string, body: RecordOutcomeRequest) =>
    api.post<RecommendationOutcome>(`${V1}/recommendations/${id}/outcome`, body),

  // Outcome evidence — a two-phase upload. `initiate` and `complete` are
  // ordinary path segments; they were once `evidence:initiate` and
  // `:attachmentId:complete`, which put two adjacent parameters in one segment
  // and made `complete` reject every call.
  evidenceList: (recommendationId: string) =>
    api.get<EvidenceListResponse>(`${V1}/recommendations/${recommendationId}/outcome/evidence`),
  initiateEvidence: (recommendationId: string, body: InitiateEvidenceRequest) =>
    api.post<InitiateEvidenceResponse>(
      `${V1}/recommendations/${recommendationId}/outcome/evidence/initiate`,
      body,
    ),
  completeEvidence: (recommendationId: string, attachmentId: string) =>
    api.post<EvidenceAttachment>(
      `${V1}/recommendations/${recommendationId}/outcome/evidence/${attachmentId}/complete`,
      {},
    ),
  evidenceDownloadUrl: (recommendationId: string, attachmentId: string) =>
    api.get<EvidenceDownloadUrlResponse>(
      `${V1}/recommendations/${recommendationId}/outcome/evidence/${attachmentId}/url`,
    ),
  deleteEvidence: (recommendationId: string, attachmentId: string) =>
    api.del<void>(`${V1}/recommendations/${recommendationId}/outcome/evidence/${attachmentId}`),

  // Outcome analytics. `sinceDays` is validated server-side and REJECTED with
  // 400 when non-positive, so an "all time" selection must omit the parameter
  // entirely rather than send 0.
  outcomeSummary: (sinceDays?: number) =>
    api.get<OutcomeSummaryResponse>(
      `${V1}/recommendations/outcomes/stats`,
      sinceDays ? ({ sinceDays } as Q) : undefined,
    ),
  outcomeSummaryAs: (tenantId: string, sinceDays?: number) =>
    api.getAs<OutcomeSummaryResponse>(
      `${V1}/recommendations/outcomes/stats`,
      tenantId,
      sinceDays ? ({ sinceDays } as Q) : undefined,
    ),
  outcomePolicyLearning: (sinceDays?: number) =>
    api.get<PolicyLearningResponse>(
      `${V1}/recommendations/outcomes/policy-learning`,
      sinceDays ? ({ sinceDays } as Q) : undefined,
    ),
  // `bucket` is omitted rather than defaulted client-side, so the server's
  // choice of 'week' stays the single source of that decision.
  outcomeTrend: (sinceDays?: number, bucket?: OutcomeTrendBucket) =>
    api.get<OutcomeTrendResponse>(`${V1}/recommendations/outcomes/trend`, {
      ...(sinceDays ? { sinceDays } : {}),
      ...(bucket ? { bucket } : {}),
    } as Q),
  outcomeRootCauses: (sinceDays?: number) =>
    api.get<RootCauseStatsResponse>(
      `${V1}/recommendations/outcomes/root-causes`,
      sinceDays ? ({ sinceDays } as Q) : undefined,
    ),
  outcomeConfidence: (sinceDays?: number) =>
    api.get<ConfidenceStatsResponse>(
      `${V1}/recommendations/outcomes/confidence`,
      sinceDays ? ({ sinceDays } as Q) : undefined,
    ),
  outcomeTiming: (sinceDays?: number) =>
    api.get<TimingStatsResponse>(
      `${V1}/recommendations/outcomes/timing`,
      sinceDays ? ({ sinceDays } as Q) : undefined,
    ),
  outcomePerformers: (sinceDays?: number) =>
    api.get<PerformerStatsResponse>(
      `${V1}/recommendations/outcomes/performers`,
      sinceDays ? ({ sinceDays } as Q) : undefined,
    ),
  outcomeBusinessImpact: (sinceDays?: number) =>
    api.get<BusinessImpactSummaryResponse>(
      `${V1}/recommendations/outcomes/business-impact`,
      sinceDays ? ({ sinceDays } as Q) : undefined,
    ),
  // Deliberately takes no period: coverage is a current-state count of stored
  // attachments, not a windowed metric. The API accepts no sinceDays here.
  outcomeEvidenceCoverage: () =>
    api.get<EvidenceCoverageResponse>(`${V1}/recommendations/outcomes/evidence-coverage`),

  // vehicles
  // `resolution` is sent explicitly rather than left to the server default, so
  // the query key and the request agree on what was asked for — `auto` would
  // otherwise cache two different resolutions under indistinguishable keys.
  vehicleTelemetry: (
    id: string,
    q: { parameters: string; hours: number; resolution: 'auto' | 'raw' | 'hourly' },
  ) => api.get<TelemetryHistoryResponse>(`${V1}/vehicles/${id}/telemetry`, q as Q),
  vehicleTelemetryHealth: (id: string, q: { hours: number }) =>
    api.get<TelemetryHealthResponse>(`${V1}/vehicles/${id}/telemetry/health`, q as Q),
  vehicleTelemetryTrends: (id: string, q: { days: number }) =>
    api.get<TelemetryTrendResponse>(`${V1}/vehicles/${id}/telemetry/trends`, q as Q),
  vehicleTrack: (id: string, q: { hours: number }) =>
    api.get<VehicleTrackResponse>(`${V1}/vehicles/${id}/track`, q as Q),
  vehicleTrips: (id: string, q: { hours: number; day?: string }) =>
    api.get<VehicleTripsResponse>(`${V1}/vehicles/${id}/trips`, q as Q),
  /** Drivers' hours as the tachograph last reported them. Gated on TACHOGRAPH. */
  vehicleTacho: (id: string, q: { hours: number }) =>
    api.get<VehicleTachoResponse>(`${V1}/vehicles/${id}/telemetry/tacho`, q as Q),
  /**
   * The same tachograph's work state day by day, as bands on a 24-hour axis.
   *
   * Raw only, so the window is bounded by retention and the response says when
   * a longer request was cut — tacho parameters have no hourly rollup by design.
   */
  tachoActivity: (id: string, q: { days: number }) =>
    api.get<TachoActivityResponse>(`${V1}/vehicles/${id}/tachograph/activity`, q as Q),
  /**
   * The vehicles that chart can be drawn for — a SEPARATE list from
   * `fleetTacho`, because that one is a fixed 24 hours and this follows the
   * chart's own window.
   */
  tachoVehicles: (q: { days: number }) =>
    api.get<TachoVehiclesResponse>(`${V1}/fleet/tacho/vehicles`, q as Q),
  /**
   * The brand for a hostname, BEFORE anybody has signed in.
   *
   * The only unauthenticated call the client makes besides login and version.
   * Presentational fields only; an unrecognised host returns the platform
   * default rather than a 404.
   */
  publicTheme: (host: string) => api.get<TenantThemeDto>(`${V1}/theme`, { host } as Q),
  vehicleStates: (id: string, q: { parameters: string; hours: number; day?: string }) =>
    api.get<TelemetryStatesResponse>(`${V1}/vehicles/${id}/telemetry/states`, q as Q),
  geofences: () => api.get<GeofenceListResponse>(`${V1}/geofences`),
  /** No tenant context and no auth: it answers "what is running", not "for whom". */
  version: () => api.get<VersionResponse>(`${V1}/system/version`),
  classifyGeofence: (id: string, kind: string) =>
    api.patch<GeofenceDto>(`${V1}/geofences/${id}/classification`, { kind }),
  aiAllowance: () => api.get<AiAllowance>(`${V1}/llm/usage`),
  tenantAiBudget: (tenantId: string) =>
    api.get<TenantAiBudgetResponse>(`${V1}/platform/tenants/${tenantId}/ai-budget`),
  setTenantAiBudget: (tenantId: string, body: UpdateAiBudget) =>
    api.patch<TenantAiBudgetResponse>(`${V1}/platform/tenants/${tenantId}/ai-budget`, body),
  tenantAnomalyPublication: (tenantId: string) =>
    api.get<AnomalyPublicationResponse>(`${V1}/platform/tenants/${tenantId}/anomaly-publication`),
  setTenantAnomalyPublication: (tenantId: string, body: UpdateAnomalyPublication) =>
    api.patch<AnomalyPublicationResponse>(
      `${V1}/platform/tenants/${tenantId}/anomaly-publication`,
      body,
    ),
  fleetPositions: () => api.get<FleetPositionsResponse>(`${V1}/fleet/positions`),
  /*
   * ⚠️ The `As` variants exist for ONE caller: the dashboard reading a single
   * branch while standing in the organisation. They send `x-tenant-id`, which
   * is the same header the switcher sets, so they grant nothing that entering
   * the branch would not — and nothing passes `getAs` on a mutation.
   *
   * Without them these cards would read the PARENT, which holds no fleet (D2),
   * and print an empty card directly under figures for a branch that is not
   * empty. One page contradicting itself is the fault the recent-incident list
   * was already fixed for.
   */
  fleetPositionsAs: (tenantId: string) =>
    api.getAs<FleetPositionsResponse>(`${V1}/fleet/positions`, tenantId),
  // drivers' hours rulesets
  driversHoursRulesets: () => api.get<DriversHoursRulesetList>(`${V1}/drivers-hours-rulesets`),
  createDriversHoursRuleset: (body: CreateDriversHoursRuleset) =>
    api.post<DriversHoursRulesetList>(`${V1}/drivers-hours-rulesets`, body),
  updateDriversHoursRuleset: (id: string, body: UpdateDriversHoursRuleset) =>
    api.patch<DriversHoursRulesetList>(`${V1}/drivers-hours-rulesets/${id}`, body),
  archiveDriversHoursRuleset: (id: string) =>
    api.post<DriversHoursRulesetList>(`${V1}/drivers-hours-rulesets/${id}/archive`, {}),
  restoreDriversHoursRuleset: (id: string) =>
    api.post<DriversHoursRulesetList>(`${V1}/drivers-hours-rulesets/${id}/restore`, {}),
  // vehicle classes
  vehicleClasses: (q?: { includeArchived?: 'true' | 'false' }) =>
    api.get<VehicleClassList>(`${V1}/vehicle-classes`, (q ?? {}) as Q),
  createVehicleClass: (body: CreateVehicleClassRequest) =>
    api.post<VehicleClassDto>(`${V1}/vehicle-classes`, body),
  updateVehicleClass: (id: string, body: UpdateVehicleClassRequest) =>
    api.patch<VehicleClassDto>(`${V1}/vehicle-classes/${id}`, body),
  archiveVehicleClass: (id: string) =>
    api.post<VehicleClassDto>(`${V1}/vehicle-classes/${id}/archive`, {}),
  restoreVehicleClass: (id: string) =>
    api.post<VehicleClassDto>(`${V1}/vehicle-classes/${id}/restore`, {}),

  /*
   * Bulk vehicle import.
   *
   * ⚠️ **Two routes that take the SAME body and differ only in whether they write**, and
   * both are named for what they do rather than sharing one endpoint with a flag. A
   * `?dryRun=` parameter on one route is a boolean an integrator can omit, and the omission
   * writes — which is the wrong default for the only call on this surface that creates rows
   * in bulk. Two paths cannot be got wrong by forgetting something.
   */
  validateVehicleImport: (csv: string) =>
    api.post<VehicleImportReport>(`${V1}/vehicles/imports/validate`, { csv }),
  applyVehicleImport: (csv: string) =>
    api.post<VehicleImportReport>(`${V1}/vehicles/imports/apply`, { csv }),

  /*
   * ⚠️ **FOUR import surfaces, one report shape, and the URL is the ONLY difference.** Which
   * levels a file may carry and which ancestor columns are fixed from the session are decided
   * on the SERVER, per surface — not here — so this cannot widen what a caller may create.
   * D8a: *you may import exactly what you may create by hand*.
   *
   * Three of these had controllers and no caller until now. The API exposed them, the web app
   * called one, and the feature was unreachable at every level but the fleet account's —
   * which is the "declared and not built" shape this codebase keeps recording.
   */
  validateHierarchyImport: (csv: string) =>
    api.post<VehicleImportReport>(`${V1}/admin/imports/validate`, { csv }),
  applyHierarchyImport: (csv: string) =>
    api.post<VehicleImportReport>(`${V1}/admin/imports/apply`, { csv }),
  validatePartnerImport: (csv: string) =>
    api.post<VehicleImportReport>(`${V1}/admin/partner/imports/validate`, { csv }),
  applyPartnerImport: (csv: string) =>
    api.post<VehicleImportReport>(`${V1}/admin/partner/imports/apply`, { csv }),
  validatePlatformImport: (csv: string) =>
    api.post<VehicleImportReport>(`${V1}/platform/imports/validate`, { csv }),
  applyPlatformImport: (csv: string) =>
    api.post<VehicleImportReport>(`${V1}/platform/imports/apply`, { csv }),

  cohortComparison: (q: { vehicleClassId: string; vehicleId: string; days: number }) =>
    api.get<CohortComparisonResponse>(`${V1}/fleet/comparison`, q as Q),
  fleetCanIntelligence: (q: { days: number }) =>
    api.get<FleetCanResponse>(`${V1}/fleet/can-intelligence`, q as Q),
  // correlation configuration
  correlationConfig: () => api.get<CorrelationConfigVersionDto>(`${V1}/correlation-config`),
  correlationConfigVersions: () =>
    api.get<CorrelationConfigVersionList>(`${V1}/correlation-config/versions`),
  createCorrelationConfigVersion: (body: CreateCorrelationConfigVersionRequest) =>
    api.post<CorrelationConfigVersionDto>(`${V1}/correlation-config/versions`, body),
  activateCorrelationConfigVersion: (id: string) =>
    api.post<CorrelationConfigVersionDto>(`${V1}/correlation-config/versions/${id}/activate`),
  /** Replay the detection thresholds over stored telemetry. READ-ONLY. */
  correlationConfigDryRun: (body: DryRunRequest) =>
    api.post<DryRunResponse>(`${V1}/correlation-config/dry-run`, body),
  /** Discard a version that never went live. 409 if it has ever been active. */
  discardCorrelationConfigVersion: (id: string) =>
    api.del<void>(`${V1}/correlation-config/versions/${id}`),

  // drivers
  drivers: (q: ListParams<DriverListQuery>) => api.get<DriverListResponse>(`${V1}/drivers`, q as Q),
  driver: (id: string) => api.get<DriverDetail>(`${V1}/drivers/${id}`),
  createDriver: (body: CreateDriverRequest) => api.post<DriverListItem>(`${V1}/drivers`, body),
  updateDriver: (id: string, body: UpdateDriverRequest) =>
    api.patch<DriverListItem>(`${V1}/drivers/${id}`, body),
  archiveDriver: (id: string, note?: string) =>
    api.post<DriverListItem>(`${V1}/drivers/${id}/archive`, note ? { note } : {}),
  restoreDriver: (id: string) => api.post<DriverListItem>(`${V1}/drivers/${id}/restore`),

  vehicles: (q: ListParams<VehicleListQuery>) =>
    api.get<VehicleListResponse>(`${V1}/vehicles`, q as Q),
  vehicle: (id: string) => api.get<VehicleDetail>(`${V1}/vehicles/${id}`),
  createVehicle: (body: CreateVehicleRequest) => api.post<VehicleDetail>(`${V1}/vehicles`, body),
  updateVehicle: (id: string, body: UpdateVehicleRequest) =>
    api.patch<VehicleDetail>(`${V1}/vehicles/${id}`, body),
  archiveVehicle: (id: string, note?: string) =>
    api.post<VehicleDetail>(`${V1}/vehicles/${id}/archive`, { note }),
  restoreVehicle: (id: string) => api.post<VehicleDetail>(`${V1}/vehicles/${id}/restore`),
  vehicleEvents: (id: string, q: ListParams<VehicleEventListQuery>) =>
    api.get<FleetEventListResponse>(`${V1}/vehicles/${id}/events`, q as Q),
  vehicleIncidents: (id: string, q: ListParams<IncidentListQuery>) =>
    api.get<IncidentListResponse>(`${V1}/vehicles/${id}/incidents`, q as Q),
  vehicleRecommendations: (id: string, q: ListParams<RecommendationListQuery>) =>
    api.get<RecommendationListResponse>(`${V1}/vehicles/${id}/recommendations`, q as Q),

  // policies
  policies: (q: ListParams<PolicyListQuery>) =>
    api.get<PolicyListResponse>(`${V1}/policies`, q as Q),
  policy: (id: string) => api.get<PolicyDetail>(`${V1}/policies/${id}`),
  policyVersions: (id: string) =>
    api.get<PolicyVersionListResponse>(`${V1}/policies/${id}/versions`),
  policyActive: (id: string) => api.get<PolicyVersionDto>(`${V1}/policies/${id}/active`),
  createPolicy: (body: CreatePolicyRequest) => api.post<PolicyDetail>(`${V1}/policies`, body),
  createPolicyVersion: (id: string, body: CreatePolicyVersionRequest) =>
    api.post<PolicyVersionDto>(`${V1}/policies/${id}/versions`, body),
  backtestPolicyVersion: (id: string, versionId: string, days = 30) =>
    api.post<PolicyBacktestResponse>(`${V1}/policies/${id}/versions/${versionId}/backtest`, {
      days,
    }),
  activatePolicyVersion: (id: string, versionId: string) =>
    api.post<PolicyVersionDto>(`${V1}/policies/${id}/versions/${versionId}/activate`),
  policyLifecycle: (id: string, body: PolicyLifecycleRequest) =>
    api.post<PolicyListItem>(`${V1}/policies/${id}/lifecycle`, body),
  deletePolicy: (id: string) => api.del<void>(`${V1}/policies/${id}`),
  updatePolicyVersion: (id: string, versionId: string, body: UpdatePolicyVersionRequest) =>
    api.patch<PolicyVersionDto>(`${V1}/policies/${id}/versions/${versionId}`, body),
  discardPolicyVersion: (id: string, versionId: string) =>
    api.del<void>(`${V1}/policies/${id}/versions/${versionId}`),

  // audit
  audit: (q: ListParams<AuditListQuery>) => api.get<AuditListResponse>(`${V1}/audit`, q as Q),

  // admin
  users: (q: ListParams<AdminMemberListQuery>) =>
    api.get<AdminMemberListResponse>(`${V1}/admin/members`, q as Q),
  member: (userId: string) => api.get<AdminMemberDto>(`${V1}/admin/members/${userId}`),
  /**
   * Adds an EXISTING global identity to this tenant. A tenant administrator
   * cannot create an identity (ADR 0009 §2) — an unknown address is refused.
   */
  addMember: (body: CreateMembershipRequest) =>
    api.post<AdminMemberDto>(`${V1}/admin/members`, body),
  updateMemberRole: (userId: string, body: UpdateMembershipRequest) =>
    api.patch<AdminMemberDto>(`${V1}/admin/members/${userId}`, body),
  disableMember: (userId: string) =>
    api.post<AdminMemberDto>(`${V1}/admin/members/${userId}/disable`),
  enableMember: (userId: string) =>
    api.post<AdminMemberDto>(`${V1}/admin/members/${userId}/enable`),
  roles: () => api.get<AdminRolesResponse>(`${V1}/admin/roles`),
  permissions: () => api.get<AdminPermissionsResponse>(`${V1}/admin/permissions`),
  providerConnections: () =>
    api.get<ProviderConnectionsResponse>(`${V1}/admin/provider-connections`),
  eventProcessingHealth: () =>
    api.get<EventProcessingHealthResponse>(`${V1}/admin/event-processing`),
  eventProcessingHealthAs: (tenantId: string) =>
    api.getAs<EventProcessingHealthResponse>(`${V1}/admin/event-processing`, tenantId),

  createProviderConnection: (body: CreateProviderConnectionRequest) =>
    api.post<ProviderConnectionSecretResponse>(`${V1}/admin/provider-connections`, body),
  rotateProviderConnectionKey: (id: string) =>
    api.post<ProviderConnectionSecretResponse>(
      `${V1}/admin/provider-connections/${id}/rotate-key`,
      {},
    ),

  inboundEvents: (q: InboundEventQuery) =>
    api.get<InboundEventListResponse>(`${V1}/admin/inbound-events`, q as Q),
  inboundEvent: (id: string) => api.get<InboundEventDetail>(`${V1}/admin/inbound-events/${id}`),
  updateProviderOutbound: (id: string, body: UpdateProviderOutbound) =>
    api.patch<ProviderConnectionsResponse>(`${V1}/admin/provider-connections/${id}/outbound`, body),
  syncProviderGeofences: (id: string) =>
    api.post<ProviderSyncResult>(`${V1}/admin/provider-connections/${id}/sync-geofences`, {}),
  storeProviderCredential: (id: string, body: StoreProviderCredential) =>
    api.post<ProviderConnectionsResponse>(
      `${V1}/admin/provider-connections/${id}/credential`,
      body,
    ),

  // ── external report recipients (D23) ───────────────────────────────────────
  addExternalRecipient: (reportId: string, body: AddExternalRecipient) =>
    api.post<AddExternalRecipientResponse>(
      `${V1}/admin/reports/${reportId}/external-recipients`,
      body,
    ),
  verifyExternalRecipient: (reportId: string, recipientId: string, code: string) =>
    api.post<ExternalRecipientDto>(
      `${V1}/admin/reports/${reportId}/external-recipients/${recipientId}/verify`,
      { code },
    ),
  resendExternalRecipientCode: (reportId: string, recipientId: string) =>
    api.post<AddExternalRecipientResponse>(
      `${V1}/admin/reports/${reportId}/external-recipients/${recipientId}/resend`,
      {},
    ),
  removeExternalRecipient: (reportId: string, recipientId: string) =>
    api.del<void>(`${V1}/admin/reports/${reportId}/external-recipients/${recipientId}`),
  tenantSettings: () => api.get<AdminTenantSettingsResponse>(`${V1}/admin/settings`),
  llmSettings: () => api.get<LlmSettingsResponse>(`${V1}/admin/llm-settings`),

  // platform — tenants
  // No tenant header is sent: client.ts omits it for every /platform path.
  platformTenants: (q: ListParams<TenantListQuery>) =>
    api.get<TenantListResponse>(`${V1}/platform/tenants`, q as Q),
  platformTenant: (id: string) => api.get<TenantDetail>(`${V1}/platform/tenants/${id}`),
  createPlatformTenant: (body: CreateTenantRequest) =>
    api.post<TenantProvisioningResponse>(`${V1}/platform/tenants`, body),
  updatePlatformTenant: (id: string, body: UpdateTenantRequest) =>
    api.patch<TenantDetail>(`${V1}/platform/tenants/${id}`, body),
  suspendPlatformTenant: (id: string, note?: string) =>
    api.post<TenantDetail>(`${V1}/platform/tenants/${id}/suspend`, { note }),
  reactivatePlatformTenant: (id: string, note?: string) =>
    api.post<TenantDetail>(`${V1}/platform/tenants/${id}/reactivate`, { note }),

  // platform — users
  platformUsers: (q: ListParams<PlatformUserListQuery>) =>
    api.get<PlatformUserListResponse>(`${V1}/platform/users`, q as Q),
  platformUser: (id: string) => api.get<PlatformUserDto>(`${V1}/platform/users/${id}`),
  createPlatformUser: (body: CreatePlatformUserRequest) =>
    api.post<PlatformUserCreated>(`${V1}/platform/users`, body),
  updatePlatformUser: (id: string, body: UpdatePlatformUserRequest) =>
    api.patch<PlatformUserDto>(`${V1}/platform/users/${id}`, body),
  /**
   * Capability changes are their own verb routes, not a field on the PATCH —
   * granting platform authority must not be possible as a side effect of
   * editing a display name.
   */
  grantPlatformAdmin: (id: string, note?: string) =>
    api.post<PlatformUserDto>(`${V1}/platform/users/${id}/grant-platform-admin`, { note }),
  revokePlatformAdmin: (id: string, note?: string) =>
    api.post<PlatformUserDto>(`${V1}/platform/users/${id}/revoke-platform-admin`, { note }),

  // the partner console
  // Partner-scoped: no tenant header and no partner id — the server resolves
  // the partner from the session, which is what stops any of these being a way
  // to act inside somebody else's hierarchy.
  partnerContext: () => api.get<PartnerContextResponse>(`${V1}/admin/partner`),
  /**
   * A fleet account's vehicles, read WITHOUT switching into it.
   *
   * The existing tenant-scoped route: a partner administrator reaches it by
   * derived role (WL_CHILD_ADMIN), so this is not a second authorization path
   * and the server decides exactly as it would if they had navigated there.
   */
  partnerFleetAccountVehicles: (tenantId: string) =>
    api.getAs<VehicleListResponse>(`${V1}/vehicles`, tenantId, { pageSize: 100 } as Q),

  partnerMembers: () => api.get<PartnerMemberListResponse>(`${V1}/admin/partner/members`),
  addPartnerMember: (body: AddPartnerMemberRequest) =>
    api.post<AddPartnerMemberResponse>(`${V1}/admin/partner/members`, body),
  setPartnerMember: (userId: string, body: UpdatePartnerMemberRequest) =>
    api.patch<PartnerMemberListResponse>(`${V1}/admin/partner/members/${userId}`, body),

  // The platform's view of the same people. Not a duplicate: when a partner's
  // last administrator is deactivated nobody can open its console, so
  // self-service cannot be the only way back in.
  platformPartnerMembers: (id: string) =>
    api.get<PartnerMemberListResponse>(`${V1}/platform/white-labels/${id}/members`),
  addPlatformPartnerMember: (id: string, body: AddPartnerMemberRequest) =>
    api.post<AddPartnerMemberResponse>(`${V1}/platform/white-labels/${id}/members`, body),
  setPlatformPartnerMember: (id: string, userId: string, body: UpdatePartnerMemberRequest) =>
    api.patch<PartnerMemberListResponse>(
      `${V1}/platform/white-labels/${id}/members/${userId}`,
      body,
    ),

  partnerBranding: () => api.get<PartnerBrandingDto>(`${V1}/admin/partner/branding`),
  partnerUnits: () => api.get<PartnerUnitDefaults>(`${V1}/admin/partner/units`),
  updatePartnerUnits: (body: UpdatePartnerUnitDefaults) =>
    api.patch<PartnerUnitDefaults>(`${V1}/admin/partner/units`, body),
  updatePartnerBranding: (body: UpdatePartnerBranding) =>
    api.patch<PartnerBrandingDto>(`${V1}/admin/partner/branding`, body),
  partnerCompanies: () => api.get<PartnerCompanyListResponse>(`${V1}/admin/companies`),
  createPartnerCompany: (body: CreatePartnerCompanyRequest) =>
    api.post<CreatePartnerCompanyResponse>(`${V1}/admin/companies`, body),

  /*
   * The RESELLER level — a partner's commercial intermediaries, between it and its
   * companies. ⚠️ Not a fleet account: this console called a fleet account a "reseller"
   * until the vocabulary was corrected, and these are the level that name now belongs to.
   */
  partnerResellers: () => api.get<PartnerResellerListResponse>(`${V1}/admin/partner/resellers`),
  /**
   * Policy templates a new company can start from. Read-only, partner surface.
   *
   * ⚠️ Not reseller-filtered: a template is platform configuration rather than a partner's
   * property, and every partner starting from the same curated set is the point.
   */
  policyTemplates: () => api.get<PolicyTemplateListResponse>(`${V1}/admin/policy-templates`),
  createPartnerReseller: (body: CreatePartnerResellerRequest) =>
    api.post<PartnerResellerDto>(`${V1}/admin/partner/resellers`, body),
  renamePartnerReseller: (id: string, body: RenamePartnerResellerRequest) =>
    api.patch<PartnerResellerDto>(`${V1}/admin/partner/resellers/${id}`, body),
  withdrawPartnerReseller: (id: string) =>
    api.post<void>(`${V1}/admin/partner/resellers/${id}/withdraw`, {}),
  /*
   * The PLATFORM's view of the same resellers. ⚠️ Writable, where `platformPartnerCompanies`
   * beside it is read-only: a company is the partner's CUSTOMER, a reseller is the partner's
   * own commercial STRUCTURE and belongs to this record. It is also the only door a platform
   * administrator has — the partner console refuses them, because they hold no membership in
   * any partner.
   */
  platformPartnerResellers: (id: string) =>
    api.get<PartnerResellerListResponse>(`${V1}/platform/white-labels/${id}/resellers`),
  createPlatformPartnerReseller: (id: string, body: CreatePartnerResellerRequest) =>
    api.post<PartnerResellerDto>(`${V1}/platform/white-labels/${id}/resellers`, body),
  /*
   * A reseller's CEILING — what its partner permits it to sell.
   *
   * ⚠️ Partner-surface only. A reseller raising its own ceiling would make the partner's
   * restriction a formality, so these are not `@ResellerScoped()` on the server and the
   * card that uses them renders for the partner alone.
   */
  setPartnerResellerStatus: (resellerId: string, body: SetPartnerResellerStatusRequest) =>
    api.patch<PartnerResellerDto>(`${V1}/admin/partner/resellers/${resellerId}/status`, body),

  partnerResellerFeatures: (resellerId: string) =>
    api.get<PartnerResellerFeaturesResponse>(
      `${V1}/admin/partner/resellers/${resellerId}/features`,
    ),
  setPartnerResellerFeature: (resellerId: string, body: SetPartnerResellerFeatureRequest) =>
    api.patch<PartnerResellerFeaturesResponse>(
      `${V1}/admin/partner/resellers/${resellerId}/features`,
      body,
    ),
  clearPartnerResellerRestriction: (resellerId: string) =>
    api.del<PartnerResellerFeaturesResponse>(
      `${V1}/admin/partner/resellers/${resellerId}/features`,
    ),

  renamePlatformPartnerReseller: (
    id: string,
    resellerId: string,
    body: RenamePartnerResellerRequest,
  ) =>
    api.patch<PartnerResellerDto>(
      `${V1}/platform/white-labels/${id}/resellers/${resellerId}`,
      body,
    ),
  withdrawPlatformPartnerReseller: (id: string, resellerId: string) =>
    api.post<void>(`${V1}/platform/white-labels/${id}/resellers/${resellerId}/withdraw`, {}),

  moveCompanyReseller: (tenantId: string, body: MoveCompanyResellerRequest) =>
    api.patch<PartnerResellerListResponse>(`${V1}/admin/companies/${tenantId}/reseller`, body),

  // platform — white-label partners
  // The tier above tenants: a partner owns a subdomain, a brand and a feature
  // ceiling, and a set of companies. Platform-scoped because creating one
  // records a commercial relationship no partner may record for itself.
  platformWhiteLabels: (q: ListParams<WhiteLabelListQuery>) =>
    api.get<WhiteLabelListResponse>(`${V1}/platform/white-labels`, q as Q),
  platformWhiteLabel: (id: string) =>
    api.get<WhiteLabelPartnerDto>(`${V1}/platform/white-labels/${id}`),
  /** A partner's companies, their fleet accounts and their fleet sizes. Read-only. */
  platformWhiteLabelCompanies: (id: string) =>
    api.get<PlatformPartnerCompaniesResponse>(`${V1}/platform/white-labels/${id}/companies`),
  createPlatformWhiteLabel: (body: CreateWhiteLabelRequestInput) =>
    api.post<CreateWhiteLabelResponse>(`${V1}/platform/white-labels`, body),
  updatePlatformWhiteLabel: (id: string, body: UpdateWhiteLabelRequest) =>
    api.patch<WhiteLabelPartnerDto>(`${V1}/platform/white-labels/${id}`, body),
  platformWhiteLabelFeatures: (id: string) =>
    api.get<WhiteLabelFeaturesResponse>(`${V1}/platform/white-labels/${id}/features`),
  // PATCH, matching the tenant features route beside it. Two verbs for the
  // same shape of operation on adjacent screens would be an accident rather
  // than a reason.
  setPlatformWhiteLabelFeature: (id: string, body: UpdateWhiteLabelFeature) =>
    api.patch<WhiteLabelFeaturesResponse>(`${V1}/platform/white-labels/${id}/features`, body),

  platformAudit: (q: ListParams<PlatformAuditListQuery>) =>
    api.get<PlatformAuditListResponse>(`${V1}/platform/audit`, q as Q),

  pipelineHealth: () => api.get<PipelineHealthResponse>(`${V1}/platform/pipeline-health`),

  copilotAsk: (body: CopilotAskRequestInput) =>
    api.post<CopilotFindingDto>(`${V1}/copilot/ask`, body),
  copilotFindings: (investigationId?: string, includeDismissed = false) =>
    api.get<{ items: CopilotFindingDto[]; dismissedCount: number }>(`${V1}/copilot/findings`, {
      ...(investigationId ? { investigationId } : {}),
      ...(includeDismissed ? { includeDismissed: 'true' } : {}),
    } as Q),
  copilotInvestigations: (includeArchived: boolean) =>
    api.get<{ items: CopilotInvestigationDto[] }>(`${V1}/copilot/investigations`, {
      includeArchived: String(includeArchived),
    } as Q),
  updateCopilotInvestigation: (id: string, body: { name?: string; status?: string }) =>
    api.patch<CopilotInvestigationDto>(`${V1}/copilot/investigations/${id}`, body),
  pinCopilotFinding: (id: string, pinned: boolean) =>
    api.post<CopilotFindingDto>(`${V1}/copilot/findings/${id}/pin`, { pinned }),
  /** Clear a finding from the workspace, or bring it back. */
  dismissCopilotFinding: (id: string, dismissed: boolean) =>
    api.post<CopilotFindingDto>(`${V1}/copilot/findings/${id}/dismiss`, { dismissed }),
  /** What this tenant has asked, and what it was refused. */
  copilotUsage: (days: number) =>
    api.get<CopilotUsageResponse>(`${V1}/copilot/usage`, { days: String(days) } as Q),
  /** Record that a typed question resolved to nothing. A tally, not the words. */
  copilotUnresolved: () => api.post<void>(`${V1}/copilot/unresolved`, {}),
  /**
   * Ask the server which declared question a sentence meant.
   *
   * ⚠️ Only ever called when the BROWSER matcher has already abstained. The
   * deterministic match costs nothing and happens instantly (D1); this is the
   * bounded round-trip behind it, and it answers `NONE` rather than failing
   * whenever anything goes wrong.
   */
  copilotRoute: (question: string, skipMatch = false, windowHours?: number) =>
    api.post<{
      /*
       * ⚠️ `COMPOSED` where the model BUILT the question from the catalogue rather
       * than choosing a declared one. The SERVER decides which, from the shared
       * `takesAQueryDocument` predicate — the caller passes it through and must not
       * re-derive it, or the strip would eventually say "matched" about a question a
       * model composed.
       */
      routing: 'DIRECT' | 'MATCHED' | 'MODEL' | 'COMPOSED' | 'NONE';
      capabilityKey: string | null;
      params: Record<string, unknown>;
      reason?: string;
    }>(`${V1}/copilot/route`, { question, skipMatch, ...(windowHours ? { windowHours } : {}) }),
  copilotFinding: (id: string) => api.get<CopilotFindingDto>(`${V1}/copilot/findings/${id}`),

  /** Drivers' hours across the fleet. Gated on TACHOGRAPH. */
  fleetTacho: () => api.get<FleetTachoResponse>(`${V1}/fleet/tacho`),
  /**
   * What the anomaly detector concluded — the shadow-mode surface.
   *
   * ⚠️ Declared here rather than inlined in the page, like every other call.
   * The first version built the path in the component and omitted the `/api/v1`
   * prefix the client expects, which failed as a generic "could not load".
   */
  /** Which days this organisation works — read by anomaly baselines. */
  operatingCalendar: () => api.get<OperatingCalendarResponse>(`${V1}/admin/operating-calendar`),
  setOperatingCalendar: (body: UpdateOperatingCalendar) =>
    api.patch<OperatingCalendarResponse>(`${V1}/admin/operating-calendar`, body),

  /**
   * Whether this organisation allows an AI provider outside the platform.
   *
   * Two facts and either alone sends nothing — the platform grants the entitlement,
   * the organisation gives consent. `effective` is the only field that says whether
   * anything actually leaves.
   */
  aiConsent: () => api.get<AiConsentResponse>(`${V1}/admin/ai-consent`),
  setAiConsent: (body: UpdateAiConsent) =>
    api.patch<AiConsentResponse>(`${V1}/admin/ai-consent`, body),

  /** The catalogue of kinds the detector looks for, and which are switched off. */
  anomalyFamilies: () => api.get<AnomalyFamilySettingsResponse>(`${V1}/admin/anomaly-families`),
  setAnomalyFamilies: (body: UpdateAnomalyFamilies) =>
    api.patch<AnomalyFamilySettingsResponse>(`${V1}/admin/anomaly-families`, body),

  fleetAnomalyFindings: (
    q: ListParams<AnomalyFindingListQuery> & { days?: number; from?: string; to?: string },
  ) => api.get<AnomalyFindingListResponse>(`${V1}/fleet/anomalies/findings`, q as Q),
  fleetAnomalies: (q: { days?: number; from?: string; to?: string; limit: number }) =>
    api.get<AnomalyOverviewResponse>(`${V1}/fleet/anomalies`, q as Q),

  /** The day's news. Omitting the date means today, which is computed live. */
  dailyHeadlines: (date?: string) =>
    api.get<DailyHeadlinesResponse>(`${V1}/headlines`, date ? ({ date } as Q) : undefined),
  addFleetNewsNote: (body: CreateFleetNewsNote) =>
    api.post<FleetNewsNoteDto>(`${V1}/headlines/notes`, body),
  editFleetNewsNote: (id: string, body: UpdateFleetNewsNote) =>
    api.patch<FleetNewsNoteDto>(`${V1}/headlines/notes/${id}`, body),
  withdrawFleetNewsNote: (id: string) => api.del<void>(`${V1}/headlines/notes/${id}`),
  dailyHeadlinesAs: (tenantId: string, date?: string) =>
    api.getAs<DailyHeadlinesResponse>(
      `${V1}/headlines`,
      tenantId,
      date ? ({ date } as Q) : undefined,
    ),
  fleetTachoAs: (tenantId: string) => api.getAs<FleetTachoResponse>(`${V1}/fleet/tacho`, tenantId),
  fleetSummary: (q: FleetSummaryQuery) =>
    api.get<FleetSummaryResponse>(`${V1}/fleet/summary`, q as Q),
  /**
   * One BRANCH's summary, read from the organisation without switching into it.
   *
   * The same `getAs` the partner tree uses for vehicles — `x-tenant-id`, the
   * header the switcher sets — so it grants nothing entering the tenant would
   * not. A parent administrator resolves `PARENT_CHILD_ADMIN` inside their own
   * child, and a platform administrator is admitted anywhere.
   */
  fleetSummaryAs: (tenantId: string, q: FleetSummaryQuery) =>
    api.getAs<FleetSummaryResponse>(`${V1}/fleet/summary`, tenantId, q as Q),

  canOperations: (q: CanOperationsQuery) =>
    api.get<CanOperationsResponse>(`${V1}/fleet/can-operations`, q as Q),
  canOperationsAs: (tenantId: string, q: CanOperationsQuery) =>
    api.getAs<CanOperationsResponse>(`${V1}/fleet/can-operations`, tenantId, q as Q),

  /**
   * Which trend readings a vehicle has actually sent.
   *
   * The copilot's reading picker asks this rather than offering the fixed
   * catalogue: on an electric van, fuel level can never be answered.
   */
  reportedSeries: (vehicleId: string, hours = 168) =>
    api.get<ReportedSeriesFields>(`${V1}/vehicles/${vehicleId}/telemetry/reported-series`, {
      hours,
    } as Q),
  units: () => api.get<UnitPreferencesResponse>(`${V1}/admin/units`),
  /**
   * Grant or revoke one feature for the company you are standing in.
   *
   * PARTNER administrators only — the server refuses a tenant's own admin, for
   * the reason the platform route is platform-scoped: what a customer holds is
   * the seller's commercial decision, not a preference.
   */
  setTenantFeatureAsPartner: (body: { feature: FeatureKey; enabled: boolean }) =>
    api.patch<TenantFeaturesResponse>(`${V1}/admin/features`, body),

  /** Read-only for a tenant; granting is platform-scoped below. */
  features: () => api.get<TenantFeaturesResponse>(`${V1}/admin/features`),
  activeSessions: () => api.get<ActiveSessionsResponse>(`${V1}/platform/sessions`),
  loginSessions: (userId: string) =>
    api.get<LoginSessionsResponse>(`${V1}/platform/users/${userId}/sessions`),
  revokeLoginSession: (userId: string, familyId: string) =>
    api.post<RevokeSessionsResponse>(
      `${V1}/platform/users/${userId}/sessions/${familyId}/revoke`,
      {},
    ),
  revokeAllLoginSessions: (userId: string) =>
    api.post<RevokeSessionsResponse>(`${V1}/platform/users/${userId}/sessions/revoke-all`, {}),
  tenantFeatures: (tenantId: string) =>
    api.get<TenantFeaturesResponse>(`${V1}/platform/tenants/${tenantId}/features`),
  setTenantFeature: (tenantId: string, body: UpdateTenantFeature) =>
    api.patch<TenantFeaturesResponse>(`${V1}/platform/tenants/${tenantId}/features`, body),
  updateUnits: (body: UpdateUnitPreferences) =>
    api.patch<UnitPreferencesResponse>(`${V1}/admin/units`, body),

  // notifications — a person's own settings, available to every role
  myNotifications: () => api.get<MyNotificationSettings>(`${V1}/me/notifications`),
  updateMyNotifications: (body: UpdateMyNotifications) =>
    api.patch<MyNotificationSettings>(`${V1}/me/notifications`, body),
  beginChannelVerification: (body: BeginVerification) =>
    api.post<BeginVerificationResponse>(`${V1}/me/notifications/identities`, body),
  confirmChannelVerification: (body: ConfirmVerification) =>
    api.post<{ verified: true }>(`${V1}/me/notifications/identities/verify`, body),
  myNotificationHistory: () => api.get<DeliveryHistoryResponse>(`${V1}/me/notifications/history`),

  // notifications — tenant administration
  notificationPolicy: () =>
    api.get<TenantNotificationPolicyDto>(`${V1}/admin/notifications/policy`),
  updateNotificationPolicy: (body: UpdateTenantNotificationPolicy) =>
    api.patch<TenantNotificationPolicyDto>(`${V1}/admin/notifications/policy`, body),
  updateAlertCategory: (body: UpdateAlertCategoryPolicy) =>
    api.patch<TenantNotificationPolicyDto>(`${V1}/admin/notifications/categories`, body),
  diagnoseNotification: (body: DiagnoseRequest) =>
    api.post<DiagnoseResponse>(`${V1}/admin/notifications/diagnose`, body),
  tenantNotificationHistory: (q?: { userId?: string; channel?: string }) =>
    api.get<DeliveryHistoryResponse>(`${V1}/admin/notifications/history`, q as Q),

  sendTestNotification: (body: TestSend) =>
    api.post<TestSendResponse>(`${V1}/me/notifications/test`, body),
  previewNotification: (body: PreviewRequest) =>
    api.post<PreviewResponse>(`${V1}/admin/notifications/preview`, body),
  previewBroadcast: (body: BroadcastPreviewRequest) =>
    api.post<BroadcastPreviewResponse>(`${V1}/admin/notifications/broadcast/preview`, body),
  sendBroadcast: (body: SendBroadcast) =>
    api.post<SendBroadcastResponse>(`${V1}/admin/notifications/broadcast`, body),

  // white-label branding
  /** Every role, never refused — an unentitled tenant gets platform defaults. */
  theme: () => api.get<TenantThemeDto>(`${V1}/me/theme`),
  branding: () => api.get<TenantBrandingDto>(`${V1}/admin/branding`),
  updateBranding: (body: UpdateTenantBranding) =>
    api.patch<TenantBrandingDto>(`${V1}/admin/branding`, body),

  // notifications — platform administration
  platformChannels: () =>
    api.get<PlatformChannelsResponse>(`${V1}/platform/notifications/channels`),
  updatePlatformChannel: (body: UpdatePlatformChannel) =>
    api.patch<PlatformChannelsResponse>(`${V1}/platform/notifications/channels`, body),
  updateChannelProvider: (body: UpdateChannelProvider) =>
    api.patch<PlatformChannelsResponse>(`${V1}/platform/notifications/providers`, body),
  verifyChannelProvider: (channel: string) =>
    api.post<VerificationResult>(`${V1}/platform/notifications/providers/${channel}/verify`),
  tenantMailServer: () => api.get<TenantSmtpDto>(`${V1}/admin/mail-server`),
  updateTenantMailServer: (body: UpdateTenantSmtp) =>
    api.patch<TenantSmtpDto>(`${V1}/admin/mail-server`, body),
  verifyTenantMailServer: () => api.post<VerificationResult>(`${V1}/admin/mail-server/verify`),

  suppressions: () => api.get<SuppressionsResponse>(`${V1}/platform/notifications/suppressions`),
  clearSuppression: (id: string) =>
    api.post<SuppressionsResponse>(`${V1}/platform/notifications/suppressions/${id}/clear`),

  // notifications — the sending-domain allow-list, platform-only by design
  sendingDomains: () =>
    api.get<SendingDomainsResponse>(`${V1}/platform/notifications/sending-domains`),
  authoriseSendingDomain: (body: AuthoriseSendingDomain) =>
    api.post<SendingDomainsResponse>(`${V1}/platform/notifications/sending-domains`, body),
  // scheduled reports
  // ── sub-tenants (parent context only) ──
  /*
   * The declared query language.
   *
   * ⚠️ **The document is a CONSTANT at every call site in this app**, never
   * assembled from user input. There is no query builder, and the dashboard's
   * cards each name one grain, one or two declared dimensions and one declared
   * measure — so the server's refusals are a guard against drift rather than
   * against a caller. When a builder exists this is the function it uses.
   */
  runQuery: (query: QueryDocumentInput) => api.post<QueryResultResponse>(`${V1}/query`, { query }),
  runQueryAs: (tenantId: string, query: QueryDocumentInput) =>
    api.postReadOnlyAs<QueryResultResponse>(`${V1}/query`, tenantId, { query }),

  unresolvedVehicles: () => api.get<UnresolvedVehiclesResponse>(`${V1}/vehicles/unresolved`),
  unresolvedVehiclesAs: (tenantId: string) =>
    api.getAs<UnresolvedVehiclesResponse>(`${V1}/vehicles/unresolved`, tenantId),
  transferVehicle: (id: string, body: TransferVehicleRequest) =>
    api.post<TransferVehicleResponse>(`${V1}/vehicles/${id}/transfer`, body),

  consolidatedFleetSummary: (q: { hours: number }) =>
    api.get<ConsolidatedFleetSummaryResponse>(`${V1}/fleet/summary/consolidated`, q),

  subTenants: (includeArchived: boolean) =>
    api.get<SubTenantListResponse>(`${V1}/admin/sub-tenants`, {
      includeArchived: includeArchived ? 'true' : 'false',
    }),
  subTenantLifecycle: (
    id: string,
    action: 'suspend' | 'reactivate' | 'archive' | 'restore',
    body: SubTenantLifecycleRequest,
  ) => api.post<SubTenantDto>(`${V1}/admin/sub-tenants/${id}/${action}`, body),
  createSubTenant: (body: CreateSubTenantRequest) =>
    api.post<CreateSubTenantResponse>(`${V1}/admin/sub-tenants`, body),
  updateSubTenant: (id: string, body: UpdateSubTenantRequest) =>
    api.patch<SubTenantDto>(`${V1}/admin/sub-tenants/${id}`, body),

  scheduledReports: () => api.get<ScheduledReportsResponse>(`${V1}/admin/reports`),
  createScheduledReport: (body: CreateScheduledReport) =>
    api.post<ScheduledReportDto>(`${V1}/admin/reports`, body),
  updateScheduledReport: (id: string, body: UpdateScheduledReport) =>
    api.patch<ScheduledReportDto>(`${V1}/admin/reports/${id}`, body),
  requestScheduledReportRun: (id: string) =>
    api.post<{ runId: string; alreadyRunning: boolean; period: string }>(
      `${V1}/admin/reports/${id}/run`,
      {},
    ),
  scheduledReportRuns: (id: string) =>
    api.get<ScheduledReportRunsResponse>(`${V1}/admin/reports/${id}/runs`),
  downloadReportRun: (runId: string) =>
    api.get<ReportDownload>(`${V1}/admin/reports/runs/${runId}/download`),

  // notifications — key rotation for the one thing sealed in the database
  credentialRotation: () =>
    api.get<CredentialRotationStatus>(`${V1}/platform/notifications/credential-rotation`),
  resealCredentials: () =>
    api.post<CredentialRotationResult>(
      `${V1}/platform/notifications/credential-rotation/reseal`,
      {},
    ),

  revokeSendingDomain: (id: string, body: RevokeSendingDomain) =>
    api.post<SendingDomainsResponse>(
      `${V1}/platform/notifications/sending-domains/${id}/revoke`,
      body,
    ),

  // llm
  prompts: () => api.get<LlmPromptsResponse>(`${V1}/llm/prompts`),
  /*
   * Platform-scoped: `LlmPrompt` has no tenant, so activating a version changes
   * the wording every tenant receives. These deliberately send no x-tenant-id.
   */
  platformPrompts: () => api.get<LlmPromptsResponse>(`${V1}/platform/llm/prompts`),
  createPrompt: (body: CreatePromptRequest) =>
    api.post<LlmPromptDto>(`${V1}/platform/llm/prompts`, body),
  deletePrompt: (promptId: string) => api.del<void>(`${V1}/platform/llm/prompts/${promptId}`),
  setPromptKey: (body: PromptKeyOverrideRequest) =>
    api.patch<LlmSettingsResponse>(`${V1}/admin/llm-settings/prompt-key`, body),
  createPromptVersion: (promptId: string, body: CreatePromptVersionRequest) =>
    api.post<LlmPromptVersionDto>(`${V1}/platform/llm/prompts/${promptId}/versions`, body),
  updatePromptVersion: (promptId: string, versionId: string, body: UpdatePromptVersionRequest) =>
    api.patch<LlmPromptVersionDto>(
      `${V1}/platform/llm/prompts/${promptId}/versions/${versionId}`,
      body,
    ),
  activatePromptVersion: (promptId: string, versionId: string) =>
    api.post<LlmPromptVersionDto>(
      `${V1}/platform/llm/prompts/${promptId}/versions/${versionId}/activate`,
    ),
  discardPromptVersion: (promptId: string, versionId: string) =>
    api.del<void>(`${V1}/platform/llm/prompts/${promptId}/versions/${versionId}`),
  executions: (q: ListParams<LlmExecutionListQuery>) =>
    api.get<LlmExecutionListResponse>(`${V1}/llm/executions`, q as Q),
  execution: (id: string) => api.get<LlmExecutionDetail>(`${V1}/llm/executions/${id}`),
  explanationHistory: (recommendationId: string) =>
    api.get<LlmExplanationHistoryResponse>(`${V1}/llm/recommendations/${recommendationId}/history`),
  regenerate: (recommendationId: string) =>
    api.post<RegenerateExplanationResponse>(
      `${V1}/llm/recommendations/${recommendationId}/regenerate`,
    ),
};

/**
 * Account invitations — the only endpoints on this client with NO session.
 *
 * ⚠️ Both are reached by somebody who has no account to sign in with yet, which
 * is the whole point. `skipSessionExpiry` keeps a 404 an ordinary failure: the
 * shared 401 handler would otherwise treat an answer from an unauthenticated
 * route as an expired session and bounce the reader to `/login`, losing the
 * token in the URL and with it their only way in.
 */
export const invitationsApi = {
  describe: (token: string) =>
    api.get<InvitationSubjectResponse>(`${V1}/auth/invitations/${encodeURIComponent(token)}`),
  accept: (token: string, password: string) =>
    api.post<void>(
      `${V1}/auth/invitations/${encodeURIComponent(token)}/accept`,
      { password },
      { skipSessionExpiry: true },
    ),
};

/**
 * The internal log store (§5).
 *
 * ⚠️ **PLATFORM-SCOPED on the server, and no tenant header is sent.** One store
 * holds every tenant's diagnostics, so `tenantId` here is a FILTER over the logs
 * — "lines about this tenant" — never a scope the caller is confined to. The two
 * look identical in a query string and mean opposite things, which is why this
 * says so at every layer that touches them.
 */
export const logsApi = {
  search: (q: Record<string, unknown>) => api.get<LogSearchResponse>(`${V1}/platform/logs`, q),
  /*
   * ⚠️ The `ref` is OPAQUE and already URL-safe (base64url), but it is encoded
   * anyway: a caller that stopped doing so would work until the encoding changed,
   * and a partially-encoded path segment is a 404 that looks like a missing row.
   */
  line: (ref: string) =>
    api.get<LogDetailResponse>(`${V1}/platform/logs/${encodeURIComponent(ref)}`),
  timeline: (q: Record<string, unknown>) =>
    api.get<LogTimelineResponse>(`${V1}/platform/logs/timeline`, q),
  /*
   * ⚠️ A DOWNLOAD rather than a read, so it does not go through `api` at all —
   * the answer is a CSV or NDJSON file and `request` parses JSON. It also must
   * not be the grid's own client-side export: that writes the rows currently
   * loaded, which on a keyset-paged window is a file that looks complete and is
   * not. The server refuses above its cap rather than truncating, and this is how
   * that refusal reaches the reader.
   */
  export: (q: Record<string, unknown>) =>
    downloadWithSession(`${V1}/platform/logs/export`, q, 'oip-logs.csv'),
  /*
   * §6's dashboard. Served from the summary tables, so it is cheap however many
   * people have it open — unlike the search beside it.
   */
  dashboard: (window: string) =>
    api.get<LogDashboardResponse>(`${V1}/platform/logs/dashboard`, { window }),
};

/**
 * §1's runtime log levels.
 *
 * ⚠️ **A DIFFERENT PREFIX FROM `logs`, and deliberately so.** Mounted under it,
 * `GET .../logs/levels` would sit beside `GET .../logs/:ref` and which one
 * answered would depend on controller registration order — a collision that
 * resolves differently as modules move, whose symptom is the detail route
 * answering a request for the levels.
 */
export const logLevelsApi = {
  state: () => api.get<LogLevelState>(`${V1}/platform/log-levels`),
  /** Logger names in the replica that answers — a typing aid, not a permitted set. */
  services: () => api.get<{ services: string[] }>(`${V1}/platform/log-levels/services`),
  set: (body: LogLevelSetRequest) => api.put<LogLevelState>(`${V1}/platform/log-levels`, body),
  clear: (service: string) =>
    api.del<LogLevelState>(`${V1}/platform/log-levels/${encodeURIComponent(service)}`),
};

/**
 * §7's alerts.
 *
 * ⚠️ **PLATFORM-SCOPED and no tenant header**, like the log store beside it: an
 * alert names a queue, a writer or a provider connection across every tenant, so
 * it has no tenant to be scoped to.
 */
export const platformAlertsApi = {
  list: (q: Record<string, unknown>) =>
    api.get<PlatformAlertListResponse>(`${V1}/platform/alerts`, q),
  /*
   * ⚠️ Two routes rather than one status write. Acknowledging and resolving are
   * different ACTS — one says "I am on it" and leaves the alert live, the other
   * says it is dealt with — and a generic status field would let a client set
   * RESOLVED or REOPENED directly, which is the evaluator's to decide.
   */
  acknowledge: (id: string) =>
    api.post<void>(`${V1}/platform/alerts/${encodeURIComponent(id)}/acknowledge`, {}),
  resolve: (id: string) =>
    api.post<void>(`${V1}/platform/alerts/${encodeURIComponent(id)}/resolve`, {}),
};
